import { z } from "zod";
import { NUMBER_REGEX } from "@/config/constants";
import { DEFAULT_ENABLED_EXTENSION_SITES } from "@/config/extensionSites";

// Tenant name must contain at least one letter — blocks all-digit / all-space / all-special-char entries.
const TENANT_NAME_HAS_LETTER = /\p{L}/u;
const SLUG_MAX_LENGTH = 63;
// Lowercase letters with at most one hyphen, and never at either end. No
// digits — the slug is the tenant's subdomain and its database name, and the
// server applies exactly this rule (`_TENANT_SLUG_RE` in
// server/app/api/v2/admin/schema/api.py).
const SLUG_REGEX = /^[a-z]+(?:-[a-z]+)?$/;

/**
 * Add/edit tenant form rules.
 *
 * Two fields are asked for only in the case they mean something, and the
 * `superRefine` at the bottom is the only place that says which case: the seat
 * licence, which caps invitations an AD tenant never issues, and the first
 * admin, who is reached by address on an email tenant and named by corporate ID
 * on an AD one. `sign-in method` itself is asked once, at creation — the update
 * route carries no such field.
 *
 * `existingSlug` is the slug the tenant already has, passed on edit only. It is
 * accepted as-is, whatever its shape: tenants created before the one-hyphen
 * rule carry slugs today's regex rejects, and without this the SuperAdmin could
 * not save a name or a seat count on those tenants at all. Any *new* value in
 * the field still has to clear the rule.
 *
 * `requireFirstAdmin` is true on add and false on edit. A tenant is created
 * with an admin or it is created unusable, so add insists on one. An existing
 * tenant already has its admins, they are managed on its Users screen, and the
 * update route carries no admin field at all — so Edit asks for none of the
 * three and insisting again there only made a tenant unsaveable.
 */
export const buildTenantFormSchema = (existingSlug = "", { requireFirstAdmin = true } = {}) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, "Tenant name is required")
        .min(2, "Tenant name must be at least 2 characters")
        .max(150, "Tenant name must not exceed 150 characters")
        .refine((val) => TENANT_NAME_HAS_LETTER.test(val), "Tenant name must contain letters and cannot be only numbers, spaces or special characters"),

      slug: z
        .string()
        .trim()
        .min(1, "Slug is required")
        .refine((val) => !/\s/.test(val), "Slug must not contain spaces")
        .refine((val) => !/[0-9]/.test(val), "Slug must not contain numbers")
        .min(2, "Slug must be at least 2 characters")
        .max(SLUG_MAX_LENGTH, `Slug must not exceed ${SLUG_MAX_LENGTH} characters`)
        .refine(
          (val) => val === existingSlug || SLUG_REGEX.test(val),
          "Slug can contain only lowercase letters and at most one hyphen between them — not at the start or end",
        ),

      region: z.string().min(1, "Country is required"),
      currency: z.string().min(1, "Currency is required").max(5),

      // The seat licence. Empty is valid here and required by the refinement at
      // the bottom of this schema, because it is a licence on invited accounts
      // and an AD tenant issues none — every invite route is closed for one
      // (`require_non_ad_tenant`), so the number would cap nothing. The numeric
      // rules stay here, in one place, whether or not the field is asked for.
      maxUsers: z
        .string()
        .trim()
        .refine((val) => val === "" || NUMBER_REGEX.test(val), "Only numbers are allowed")
        .refine((val) => val === "" || Number(val) > 0, "Max user must be greater than 0")
        .refine((val) => val === "" || Number(val) <= 1000000, "Max user must not exceed 1,000,000")
        .transform((val) => (val === "" ? undefined : Number(val))),

      // How many flows the tenant may define — same limit
      // `server/app/api/v2/admin/schema/api.py::CreateTenantRequest` enforces.
      // The per-flow status cap is fixed at 15 platform-wide and not
      // SuperAdmin-configurable, so there is no field for it here.
      maxCandidateStatusFlows: z
        .string()
        .min(1, "Max status flows is required")
        .regex(NUMBER_REGEX, "Only numbers are allowed")
        .transform((val) => Number(val))
        .refine((val) => val >= 1, "Must be at least 1")
        .refine((val) => val <= 100, "Must not exceed 100"),

      // The tenant's first admin. Which of the three fields below is required
      // depends on `adLoginEnabled`, and the refinement at the bottom of this
      // schema is the only place that says so — the server's
      // `_first_admin_is_reachable` is the same rule, in the same words.
      //
      // Optional here rather than required: an AD tenant sends none of the two
      // invitation fields, because nobody is invited.
      adminName: z.string().trim().max(150, "Admin name must not exceed 150 characters").optional().or(z.literal("")),

      adminEmail: z.string().trim().max(150, "Admin email must be 150 characters or fewer").optional().or(z.literal("")),

      // The corporate ID of the employee who arrives holding the Admin role on
      // their first directory sign-in. An AD tenant's only route to an admin: a
      // directory sign-in otherwise lands on the default-assignment role, and an
      // AD tenant's invite routes are shut.
      adminEmployeeId: z.string().trim().max(150, "Corporate ID must not exceed 150 characters").optional().or(z.literal("")),

      // Dynamic-DB routing: 'shared' (current model) or 'dedicated'
      // (provisions a brand-new DB on the platform Postgres server).
      dbMode: z.enum(["shared", "dedicated"]).default("shared"),

      // SuperAdmin-controlled: which browser-extension sites this tenant can
      // use. Empty array is valid — it means no extension access at all.
      enabledExtensionSites: z.array(z.string()).default([]),
      // SuperAdmin-controlled (ATH-I1765): which countries this tenant is seeded
      // predefined compliance document sets for, as ISO alpha-2 codes. Empty is
      // valid and is the default — the countries a client recruits in is a
      // commercial fact, not something to infer from the tenant's own region.
      complianceCountryMasters: z.array(z.string()).default([]),
      // SuperAdmin sourcing entitlement. False = the tenant uses the platform's
      // API keys (today's behaviour for everyone); true = it brings its own and
      // gets the Configure APIs screen.
      sourcingUsesOwnApiKeys: z.boolean().default(false),
      // Does this tenant sign in through Active Directory. SuperAdmin-owned on
      // purpose: a tenant admin must not be able to lock their own organisation
      // out of its sign-in, in either direction.
      adLoginEnabled: z.boolean().default(false),
    })
    .superRefine((form, ctx) => {
      // The seat licence is required of an email tenant and meaningless to an
      // AD one — the form asks for it in exactly the case it is enforced.
      if (!form.adLoginEnabled && form.maxUsers === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["maxUsers"], message: "Max user is required" });
      }

      // One rule, both sign-in methods: the tenant has to be created with an
      // admin somebody can actually reach. Written once here, because a form
      // that asked for less than the route does is a 422 the SuperAdmin
      // cannot read.
      if (!requireFirstAdmin) return;

      if (form.adLoginEnabled) {
        if (!form.adminEmployeeId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["adminEmployeeId"],
            message: "Corporate ID is required — it names the employee who will hold the Admin role",
          });
        }
        return;
      }

      if (!form.adminName) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["adminName"], message: "Admin name is required" });
      } else if (form.adminName.length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["adminName"], message: "Admin name must be at least 2 characters" });
      }

      if (!form.adminEmail) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["adminEmail"], message: "Admin email is required" });
        // zod's own email rule, asked rather than restated as a second regex.
      } else if (!z.string().email().safeParse(form.adminEmail).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["adminEmail"], message: "Invalid email address" });
      }
    });

// Add-tenant callers, and anyone who only needs the rules for a brand-new slug.
export const tenantFormSchema = buildTenantFormSchema();

export const tenantFormDefaultValues = {
  name: "",
  slug: "",
  region: "",
  maxUsers: "",
  maxCandidateStatusFlows: "",
  adminName: "",
  adminEmail: "",
  adminEmployeeId: "",
  dbMode: "shared",
  currency: "",
  enabledExtensionSites: DEFAULT_ENABLED_EXTENSION_SITES,
  complianceCountryMasters: [],
  sourcingUsesOwnApiKeys: false,
  adLoginEnabled: false,
};

export const deleteTenantReasonSchema = z.object({
  reason: z.string().trim().min(10, "Reason must be at least 10 characters").max(500, "Reason must not exceed 500 characters"),
});

export const deleteTenantReasonDefaultValues = {
  reason: "",
};
